import { func, object, string } from 'prop-types';
import dynamic from 'next/dynamic';
import React, { Component } from 'react';
import { aggregateEvents, createEventHandlers } from './helpers';

// Dynamically load ReactJWPlayer without SSR
const ReactJWPlayer = dynamic(() => import('react-jw-player'), { ssr: false });

export const JW_URL = 'https://content.jwplatform.com';

function requiredMediaPropsCheck(props, propName, componentName) {
  if (!props.videoId && !props.playlist) {
    return new Error(
      `One of 'videoId' or 'playlist' is required by '${componentName}' component.`
    );
  }
  return undefined;
}

/**
 * Our JWVideo component is a thin wrapper around the ReactJWPlayer component.
 *
 * We need to get the actual player object so event handlers have access to it
 * for reporting/tracking purposes.
 *
 * The key event handlers necessary for the component to function are stored in
 * instance methods named 'on*' and are then grouped together into an
 * eventHandlers attr so we can spread the handlers onto the ReactJWPlayer
 * component later in a little cleaner way. See bindEventHandlers()
 *
 * @class JWVideo
 * @extends {Component}
 */
class JWVideo extends Component {
  // Counters for player placements
  static counters = {};

  constructor(props) {
    super(props);

    const { placement, category } = props;

    this.bindEventHandlers();

    // updated in the onReady event handler to refer to the current JW instance.
    this.player = null;

    // we need to know what 'category' (nee vertical) this player is in.
    this.categoryContext = category;

    this.playerId = '';

    if (typeof window !== 'undefined') {
      // Track placement counts in the browser context. We do not want to do this
      // server side otherwise the counts will be shared!
      // Note: ReactJWPlayer is dynamically loaded without SSR so we don't need
      // the id server-side anyway.
      if (JWVideo.counters[placement] === undefined) {
        // Initialize placement
        JWVideo.counters[placement] = 0;
      }
      // Increment counter...
      const count = JWVideo.counters[placement] + 1;
      // And save
      JWVideo.counters[placement] = count;
      // Generate unique id
      this.playerId = `jw-player-${placement.replace(/_/g, '-')}-${count}`;
    }
  }

  componentWillUnmount() {
    const { placement } = this.props;
    const count = JWVideo.counters[placement];
    if (count) {
      // Decrement counter when video unmounts
      JWVideo.counters[placement] = count - 1;
    }
  }

  // The following 'on*' methods are required event handlers for ReactJWPlayer
  // in order for this component to function. If we need more handlers, define
  // them here and refer to them in bindEventHandlers().
  //
  // needed so we can update the player attr to the JW instance
  onReady(e) {
    this.player = window.jwplayer(this.playerId);
    // be sure to fire custom event if it exists. have to do this manually
    // because we overwrite it in bindEventHandlers().
    const { onReady: handler } = this.props.customEventHandlers;
    if (handler) {
      handler(e, this.player, this.categoryContext);
    }

    // CLEANUP: temporary patch to allow for a custom onBeforePlay handler.
    // react-jw-player does not call the onBeforePlay that is passed due to a
    // special case.
    const { onBeforePlay } = this._eventHandlers;
    if (onBeforePlay) {
      this.player.on('beforePlay', onBeforePlay);
    }
  }

  // handles setting up the JW event handlers. separated out for readability.
  bindEventHandlers() {
    const { customEventHandlers } = this.props;

    // Create all of the event handlers for ReactJWPlayer using any passed
    // custom event handlers (via props), and giving precedence to existing key
    // custom event handlers that are required for the component to work.
    this.eventHandlers = Object.assign(
      {},
      createEventHandlers(customEventHandlers),
      {
        // base/required event handlers
        onReady: this.onReady
      }
    );

    // Iterate over our event handlers and dynamically bind them to the
    // instance, storing them on a new _-prefixed prop.
    this._eventHandlers = Object.keys(this.eventHandlers).reduce(
      (acc, curr) =>
        Object.assign({}, acc, {
          // only attempt to bind event handlers that are defined
          [curr]: this.eventHandlers[curr]
            ? this.eventHandlers[curr].bind(this)
            : undefined
        }),
      {}
    );
  }

  render() {
    const {
      playerScript,
      licenseKey,
      videoId,
      placement,
      generatePrerollUrl,
      customProps
    } = this.props;

    const playlist = this.props.playlist || `${JW_URL}/feeds/${videoId}.json`;

    return (
      <div className={`JWVideo JWVideo--${placement}`}>
        <ReactJWPlayer
          playerId={this.playerId}
          licenseKey={licenseKey}
          playerScript={playerScript}
          playlist={playlist}
          customProps={customProps}
          generatePrerollUrl={generatePrerollUrl}
          {...this._eventHandlers}
        />
      </div>
    );
  }
}

JWVideo.aggregateEvents = aggregateEvents;

JWVideo.defaultProps = {
  videoId: undefined,
  playlist: undefined,
  placement: 'default',
  licenseKey: '',
  generatePrerollUrl: undefined,
  customProps: undefined,
  customEventHandlers: {}
};

JWVideo.propTypes = {
  playerScript: string.isRequired,
  licenseKey: string,
  videoId: requiredMediaPropsCheck,
  playlist: requiredMediaPropsCheck,
  placement: string,
  category: string.isRequired,
  generatePrerollUrl: func,
  customProps: object, // eslint-disable-line
  customEventHandlers: object // eslint-disable-line
};

export default JWVideo;
